import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FOOTER_APP_STORE_LINKS,
  FOOTER_BRAND,
  FOOTER_CONTACT,
} from '../config/footerContact';
import { FOOTER_QUICK_LINKS } from '../config/footerQuickLinks';
import { LEGAL_LINKS } from '../config/legalLinks';
import { appointmentBookingPathForCountry } from '../config/api';
import { useClientCountry } from '../hooks/useClientCountry';
import { getFooterCopyrightText } from './SiteCopyrightBar';
import cwLogo from '../assets/cw-red-logo.svg';
import './SiteFooter.css';

export const SiteFooter: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { country } = useClientCountry();
  const bookingPath = appointmentBookingPathForCountry(country);

  const goTo = (path: string) => {
    if (path === '/appointments') {
      navigate(bookingPath);
      return;
    }
    navigate(path);
  };

  const phoneHref = FOOTER_CONTACT.phone.replace(/\s/g, '');

  return (
    <footer className="site-footer">
      <div className="site-footer-card">
        <div className="site-footer-inner">
          <div className="site-footer-grid">
            <div className="site-footer-brand">
              <button type="button" className="site-footer-logo-btn" onClick={() => navigate('/')}>
                <img src={cwLogo} alt="Canadawale Yar™" className="site-footer-logo-img" />
              </button>

              <div className="site-footer-brand-copy">
                <p className="site-footer-brand-headline">{FOOTER_BRAND.headline}</p>
                <p className="site-footer-brand-subheadline">{FOOTER_BRAND.subheadline}</p>
              </div>

              <div className="site-footer-app-downloads">
                <a
                  href={FOOTER_APP_STORE_LINKS.ios}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-footer-app-link"
                >
                  <i className="ti ti-brand-apple site-footer-app-link__icon" aria-hidden />
                  Canadawale Yar for iOS
                </a>
                <a
                  href={FOOTER_APP_STORE_LINKS.android}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-footer-app-link"
                >
                  <i className="ti ti-brand-android site-footer-app-link__icon" aria-hidden />
                  Canadawale Yar for Android
                </a>
              </div>

              <div className="site-footer-social" aria-label="Social links">
                <a
                  href={FOOTER_CONTACT.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-footer-social-link"
                  aria-label="LinkedIn"
                >
                  <i className="ti ti-brand-linkedin" aria-hidden />
                </a>
                <a
                  href={FOOTER_CONTACT.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-footer-social-link"
                  aria-label="Instagram"
                >
                  <i className="ti ti-brand-instagram" aria-hidden />
                </a>
                <a
                  href={`https://wa.me/${FOOTER_CONTACT.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-footer-social-link"
                  aria-label="WhatsApp"
                >
                  <i className="ti ti-brand-whatsapp" aria-hidden />
                </a>
              </div>

              <ul className="site-footer-brand-contact">
                <li>
                  <a href={`tel:${phoneHref}`}>
                    <i className="ti ti-phone" aria-hidden />
                    {FOOTER_CONTACT.phone}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${FOOTER_CONTACT.email}`}>
                    <i className="ti ti-mail" aria-hidden />
                    {FOOTER_CONTACT.email}
                  </a>
                </li>
              </ul>
            </div>

            <nav className="site-footer-col" aria-label="Quick links">
              <h3 className="site-footer-col-head">Quick links</h3>
              <div className="site-footer-links">
                {FOOTER_QUICK_LINKS.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    className="site-footer-link-btn"
                    onClick={() => goTo(item.path)}
                  >
                    {item.label}
                  </button>
                ))}
                {isAuthenticated ? (
                  <button
                    type="button"
                    className="site-footer-link-btn"
                    onClick={() => void logout()}
                  >
                    Sign out
                  </button>
                ) : (
                  <button type="button" className="site-footer-link-btn" onClick={() => navigate('/login')}>
                    Sign in
                  </button>
                )}
              </div>
            </nav>

            <nav className="site-footer-col" aria-label="Policy pages">
              <h3 className="site-footer-col-head">Policy pages</h3>
              <div className="site-footer-links">
                {LEGAL_LINKS.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="site-footer-link"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </nav>
          </div>

          <div className="site-footer-divider" aria-hidden />

          <div className="site-footer-bottom">
            <p className="site-footer-copy">{getFooterCopyrightText()}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
